import type { User } from "@/features/auth/model/authTypes";

/**
 * Token service for managing authentication tokens
 */
const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";

/** Fired after same-tab reads/writes to `auth_token` (the `storage` event only fires on other tabs). */
export const AUTH_TOKEN_STORAGE_SYNC_EVENT = "andy-auth-token-sync";

function notifyAuthTokenStorageSync(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AUTH_TOKEN_STORAGE_SYNC_EVENT));
}

export const tokenService = {
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  setToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
    notifyAuthTokenStorageSync();
  },

  removeToken: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    notifyAuthTokenStorageSync();
  },

  hasToken: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  getUser: (): User | null => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      // We trust our own stored value; runtime validation can be added if needed.
      return parsed as User;
    } catch {
      return null;
    }
  },

  setUser: (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  removeUser: (): void => {
    localStorage.removeItem(USER_KEY);
  },
};
